import { useQuery } from '@tanstack/react-query';
import {
  getAccountsRestsInBaseAsset,
  getExtendedMarginAccountsRestInBaseAsset,
} from '../requests';
import { type AccountsRestsByType, useAccountsByType } from '../../model';
import { useSessionStore } from '@/entities/session';
import { useAppSettingsStore } from '@/shared/lib';
import { useAppSettingsDictionary } from '@/shared/api';
import { accountsKeys } from '../keys';

const useAccountsRests = () => {
  const { baseAsset } = useAppSettingsStore();
  const { session } = useSessionStore();
  const { appSettingsData } = useAppSettingsDictionary();

  const {
    accountsInfo,
    accountsByType,
    isLoading: isAccountsByTypeLoading,
    error: accountsByTypeError,
  } = useAccountsByType();

  const { data, isLoading, error } = useQuery({
    queryKey: [
      accountsKeys.rests,
      session?.userId,
      baseAsset,
      accountsInfo?.accounts
        .map(acc => acc.accNum)
        .sort()
        .join(','),
    ],
    staleTime: Infinity,
    enabled: !!accountsByType && !!session?.userId,
    queryFn: async () => {
      if (!accountsByType || !baseAsset) {
        return null;
      }

      const accountsRests: AccountsRestsByType = {
        ewallet: [],
        invest: null,
        spot: [],
        leverage: null,
        earn: null,
      };

      const ewalletAccountsRequests = accountsByType.ewallet.map(acc =>
        getAccountsRestsInBaseAsset(acc.accNum, baseAsset)
      );

      const spotAccountsRequests = accountsByType.spot.map(acc =>
        getAccountsRestsInBaseAsset(acc.accNum, baseAsset)
      );

      const extendedRequest =
        accountsByType.invest.length > 0 ||
        accountsByType.earn.length > 0 ||
        accountsByType.leverage.length > 0
          ? getExtendedMarginAccountsRestInBaseAsset(baseAsset)
          : Promise.resolve(null);

      const [ewalletRests, spotRests, extendedRests] = await Promise.all([
        Promise.all(ewalletAccountsRequests),
        Promise.all(spotAccountsRequests),
        extendedRequest,
      ]);

      accountsRests.ewallet = ewalletRests;
      accountsRests.spot = spotRests;

      if (extendedRests) {
        const tradingInvestCodes =
          appSettingsData?.value.tradingInvestCodes || [];
        const tradingEarnCodes = appSettingsData?.value.tradingEarnCodes || [];
        const tradingLeverageCodes =
          appSettingsData?.value.tradingLeverageCodes || [];

        accountsRests.invest = {
          ...extendedRests,
          accounts: extendedRests.accounts.filter(account =>
            tradingInvestCodes.includes(account.tradingProductCode || '')
          ),
        };

        accountsRests.earn = {
          ...extendedRests,
          accounts: extendedRests.accounts.filter(account =>
            tradingEarnCodes.includes(account.tradingProductCode || '')
          ),
        };

        accountsRests.leverage = {
          ...extendedRests,
          accounts: extendedRests.accounts.filter(account =>
            tradingLeverageCodes.includes(account.tradingProductCode || '')
          ),
        };
      }

      return accountsRests;
    },
  });

  return {
    data,
    isLoading: isLoading || isAccountsByTypeLoading,
    error: error || accountsByTypeError,
  };
};

export { useAccountsRests };
